(() => {
  const node = document.getElementById('FrostCommerceConfig');
  if (!node || window.FrostCommerce) return;
  let config;
  try { config = JSON.parse(node.textContent); } catch { return; }
  const strings = config.strings;
  const root = `${config.root.replace(/\/$/, '')}/`;
  const protectionKey = '_resident_shipping_protection';
  const giftKey = '_resident_vip_gift';
  const requestKey = '_frost_vip_requested';
  let cartQueue = Promise.resolve();
  let checkoutInProgress = false;
  let cartStatus = '';
  let cartError = false;
  const nativeCartRequests = new Set();
  let quantityDebounceUntil = 0;

  // Dawn emits the real mutation promise before it starts an Ajax cart request.
  // Observe that contract rather than intercepting fetch or rewriting its cart.
  document.addEventListener('shopify:cart:lines-update', (event) => {
    if (!event.promise || typeof event.promise.then !== 'function') return;
    const pending = Promise.resolve(event.promise);
    nativeCartRequests.add(pending);
    pending.then(() => nativeCartRequests.delete(pending), () => nativeCartRequests.delete(pending));
  });

  async function waitForNativeCart() {
    const deadline = Date.now() + 15000;
    while (nativeCartRequests.size || Date.now() < quantityDebounceUntil || document.querySelector('.cart__items--disabled')) {
      const remaining = deadline - Date.now();
      if (remaining <= 0) throw new Error(strings.error);
      if (nativeCartRequests.size) {
        let timeout;
        try {
          await Promise.race([
            Promise.all([...nativeCartRequests]),
            new Promise((_, reject) => { timeout = setTimeout(() => reject(new Error(strings.error)), remaining); }),
          ]);
        } finally { clearTimeout(timeout); }
      }
      await new Promise((resolve) => setTimeout(resolve, 30));
    }
  }

  function showCartStatus(message, error = false) {
    cartStatus = message;
    cartError = error;
    document.querySelectorAll('[data-frost-commerce-status]').forEach((element) => {
      element.textContent = message;
      element.dataset.error = String(error);
      element.hidden = !message;
    });
  }

  async function cartRequest(path, body) {
    const response = await fetch(`${root}${path}`, {
      method: body ? 'POST' : 'GET',
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'application/json', ...(body ? { 'Content-Type': 'application/json' } : {}) },
      ...(body ? { body: JSON.stringify(body) } : {}),
    });
    let value;
    try { value = await response.json(); } catch { throw new Error(strings.error); }
    if (!response.ok || value.errors || value.status >= 400) {
      throw new Error(typeof value.description === 'string' ? value.description : strings.error);
    }
    return value;
  }

  function serialized(task) {
    const next = cartQueue.then(task, task);
    cartQueue = next.catch(() => {});
    return next;
  }

  function hasVipDiscount(cart) {
    return (cart.cart_level_discount_applications || []).some((discount) => discount.title?.toUpperCase() === 'VIP') ||
      (cart.discount_codes || []).some((discount) => discount.code?.toUpperCase() === 'VIP' && discount.applicable !== false);
  }

  function planPromotions(cart) {
    const attributes = cart.attributes || {};
    const main = cart.items.some((item) => item.quantity > 0 &&
      item.variant_id !== config.giftVariant && item.variant_id !== config.protectionVariant);
    const giftWanted = main && config.customerEligible && attributes[requestKey] === 'on' &&
      attributes[giftKey] !== 'off' && hasVipDiscount(cart);
    const wanted = [[config.protectionVariant, main && attributes[protectionKey] !== 'off'], [config.giftVariant, giftWanted]];
    const updates = {};
    const add = [];
    wanted.forEach(([variantId, keep]) => {
      if (!variantId) return;
      const lines = cart.items.filter((item) => item.variant_id === variantId);
      if (!keep) lines.forEach((item) => { updates[item.key] = 0; });
      else if (!lines.length) add.push({ id: variantId, quantity: 1 });
      else {
        if (lines[0].quantity !== 1) updates[lines[0].key] = 1;
        lines.slice(1).forEach((item) => { updates[item.key] = 0; });
      }
    });
    return { updates, add };
  }

  async function synchronizeCart(initialCart) {
    let cart = initialCart || await cartRequest('cart.js');
    const plan = planPromotions(cart);
    let changed = false;
    if (Object.keys(plan.updates).length) {
      cart = await cartRequest('cart/update.js', { updates: plan.updates });
      changed = true;
    }
    if (plan.add.length) {
      await cartRequest('cart/add.js', { items: plan.add });
      cart = await cartRequest('cart.js');
      changed = true;
    }
    const unpaidGift = cart.items.filter((item) => item.variant_id === config.giftVariant && item.final_line_price !== 0);
    if (unpaidGift.length) {
      cart = await cartRequest('cart/update.js', {
        updates: Object.fromEntries(unpaidGift.map((item) => [item.key, 0])),
        attributes: { [giftKey]: 'off' },
      });
      showCartStatus(strings.giftUnavailable, true);
      changed = true;
    }
    return { cart, changed };
  }

  async function refreshCart(cart) {
    const entries = [
      { id: document.querySelector('#main-cart-items')?.dataset.id, target: '#main-cart-items .js-contents' },
      { id: document.querySelector('#main-cart-footer')?.dataset.id, target: '#main-cart-footer .js-contents' },
      { id: document.querySelector('#cart-icon-bubble') ? 'cart-icon-bubble' : null, target: '#cart-icon-bubble' },
      { id: document.querySelector('cart-drawer') ? 'cart-drawer' : null, target: '#CartDrawer' },
    ].filter((entry) => entry.id);
    if (entries.length) {
      const url = new URL(config.cartUrl, location.origin);
      url.searchParams.set('sections', entries.map((entry) => entry.id).join(','));
      const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store' });
      if (!response.ok) throw new Error(strings.error);
      const sections = await response.json();
      entries.forEach((entry) => {
        if (!sections[entry.id]) return;
        const parsed = new DOMParser().parseFromString(sections[entry.id], 'text/html');
        const existing = document.querySelector(entry.target);
        const replacement = parsed.querySelector(entry.target) || parsed.querySelector('.shopify-section');
        if (existing && replacement) existing.innerHTML = replacement.innerHTML;
      });
      const drawer = document.querySelector('cart-drawer');
      const overlay = drawer?.querySelector('#CartDrawer-Overlay');
      if (overlay) overlay.onclick = () => drawer.close();
    }
    document.querySelectorAll('cart-items, cart-drawer, #main-cart-footer').forEach((element) => {
      element.classList.toggle('is-empty', cart.item_count === 0);
    });
    document.querySelectorAll('[data-frost-protection]').forEach((input) => {
      input.checked = cart.items.some((item) => item.variant_id === config.protectionVariant);
    });
    showCartStatus(cartStatus, cartError);
    document.dispatchEvent(new CustomEvent('frost:cart-updated', { detail: { cart } }));
  }

  async function reconcile() {
    const result = await synchronizeCart();
    if (result.changed) await refreshCart(result.cart);
    return result.cart;
  }

  async function applyCode(code) {
    const normalized = code.trim().toUpperCase();
    if (!normalized) throw new Error(strings.codeRequired);
    const isVip = normalized === 'VIP';
    const updated = await cartRequest('cart/update.js', {
      discount: normalized,
      attributes: { [requestKey]: isVip ? 'on' : 'off', ...(isVip ? { [giftKey]: 'on' } : {}) },
    });
    const result = await synchronizeCart(updated);
    const gift = result.cart.items.find((item) => item.variant_id === config.giftVariant);
    if (isVip) {
      if (!config.signedIn) showCartStatus(strings.vipSignIn);
      else if (!config.customerEligible) showCartStatus(strings.vipIneligible, true);
      else if (gift?.final_line_price === 0) showCartStatus(strings.giftAdded);
      else showCartStatus(strings.giftUnavailable, true);
    } else {
      const applied = (result.cart.cart_level_discount_applications || []).some((discount) => discount.title?.toUpperCase() === normalized);
      showCartStatus(applied ? strings.codeApplied : strings.codePending);
    }
    await refreshCart(result.cart);
    return result.cart;
  }

  async function setProtection(enabled) {
    const updated = await cartRequest('cart/update.js', { attributes: { [protectionKey]: enabled ? 'on' : 'off' } });
    const result = await synchronizeCart(updated);
    showCartStatus(strings.protectionSaved);
    await refreshCart(result.cart);
  }

  async function removeExtra(variant) {
    const cart = await cartRequest('cart.js');
    const updated = await cartRequest('cart/update.js', {
      updates: Object.fromEntries(cart.items.filter((item) => item.variant_id === variant).map((item) => [item.key, 0])),
      attributes: { [variant === config.protectionVariant ? protectionKey : giftKey]: 'off' },
    });
    const result = await synchronizeCart(updated);
    showCartStatus(strings.removed);
    await refreshCart(result.cart);
  }

  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (form.matches('[data-frost-discount-form]')) {
      event.preventDefault();
      const code = new FormData(form).get('discount') || '';
      const button = form.querySelector('button');
      if (button.disabled) return;
      button.disabled = true;
      serialized(() => applyCode(String(code))).catch((error) => showCartStatus(error.message, true)).finally(() => { button.disabled = false; });
      return;
    }
    if (event.submitter?.name !== 'checkout' || !form.action.includes('/cart')) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (checkoutInProgress) return;
    checkoutInProgress = true;
    const button = event.submitter;
    button.disabled = true;
    serialized(async () => {
      await waitForNativeCart();
      const result = await synchronizeCart();
      if (!result.cart.item_count) {
        await refreshCart(result.cart);
        return;
      }
      if (result.changed) await refreshCart(result.cart);
      const currentForm = document.getElementById(form.id) || form;
      const checkout = document.createElement('input');
      checkout.type = 'hidden';
      checkout.name = 'checkout';
      checkout.value = 'Checkout';
      currentForm.append(checkout);
      HTMLFormElement.prototype.submit.call(currentForm);
    }).catch((error) => showCartStatus(error.message, true)).finally(() => {
      checkoutInProgress = false;
      button.disabled = false;
    });
  }, true);

  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    const claim = target.closest('[data-frost-vip-claim]');
    if (claim) {
      event.preventDefault();
      if (claim.disabled) return;
      claim.disabled = true;
      serialized(() => applyCode('VIP')).catch((error) => showCartStatus(error.message, true)).finally(() => { claim.disabled = false; });
      return;
    }
    const removal = target.closest('cart-remove-button');
    if (!removal) return;
    const variant = Number(removal.closest('.cart-item')?.querySelector('[data-quantity-variant-id]')?.dataset.quantityVariantId);
    if (variant !== config.protectionVariant && variant !== config.giftVariant) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    serialized(() => removeExtra(variant)).catch((error) => showCartStatus(error.message, true));
  }, true);

  document.addEventListener('change', (event) => {
    if (!(event.target instanceof HTMLInputElement)) return;
    if (event.target.matches('cart-items [name="updates[]"], cart-drawer-items [name="updates[]"]')) {
      quantityDebounceUntil = Date.now() + (typeof ON_CHANGE_DEBOUNCE_TIMER === 'number' ? ON_CHANGE_DEBOUNCE_TIMER : 300) + 50;
    }
    if (event.target.matches('[data-frost-protection]')) {
      const enabled = event.target.checked;
      serialized(() => setProtection(enabled)).catch((error) => showCartStatus(error.message, true));
      return;
    }
    const variant = Number(event.target.dataset.quantityVariantId);
    if (Number(event.target.value) !== 0 || (variant !== config.protectionVariant && variant !== config.giftVariant)) return;
    event.stopImmediatePropagation();
    serialized(() => removeExtra(variant)).catch((error) => showCartStatus(error.message, true));
  }, true);

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, () => serialized(reconcile).catch((error) => showCartStatus(error.message, true)));
  }
  if (!config.designMode && document.querySelector('cart-items, cart-drawer')) {
    serialized(reconcile).catch((error) => showCartStatus(error.message, true));
  }

  const dialog = document.getElementById('FrostVipDialog');
  const invitation = document.querySelector('[data-frost-vip-invitation]');
  const preferenceKey = 'resident-vip-v1';
  const day = 86400000;
  let previousFocus;
  let step = 'email';
  let receipt = '';
  let signupBusy = false;
  let shown = false;
  let bodyOverflow = '';

  function preference() {
    try { return JSON.parse(localStorage.getItem(preferenceKey) || '{}'); } catch { return {}; }
  }
  function remember(value) {
    try { localStorage.setItem(preferenceKey, JSON.stringify({ ...preference(), ...value })); } catch {}
  }
  function showSignupError(message) {
    const element = dialog.querySelector('[data-frost-vip-error]');
    element.textContent = message;
    element.hidden = !message;
    if (message && dialog.open) element.focus({ preventScroll: true });
  }
  function fitDialog() {
    dialog.style.setProperty('--frost-vip-height', `${window.visualViewport?.height || window.innerHeight}px`);
  }
  function showStep(next) {
    step = next;
    dialog.querySelectorAll('[data-frost-vip-step]').forEach((element) => { element.hidden = element.dataset.frostVipStep !== step; });
    const title = dialog.querySelector(`[data-frost-vip-step="${step}"] [data-frost-vip-heading]`);
    dialog.setAttribute('aria-labelledby', `FrostVipTitle-${step}`);
    title.id = `FrostVipTitle-${step}`;
    title.focus({ preventScroll: true });
  }
  function openSignup() {
    if (!dialog || dialog.open) return;
    shown = true;
    invitation.hidden = true;
    previousFocus = document.activeElement;
    bodyOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    showSignupError('');
    fitDialog();
    dialog.showModal();
    showStep(step);
  }
  function closeSignup() {
    remember({ dismissedUntil: Date.now() + 30 * day });
    invitation.hidden = true;
    if (dialog.open) dialog.close();
    document.body.style.overflow = bodyOverflow;
    previousFocus?.focus?.({ preventScroll: true });
  }
  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (target?.closest('[data-frost-vip-open]')) { event.preventDefault(); openSignup(); }
    if (target?.closest('[data-frost-vip-dismiss]')) { event.preventDefault(); closeSignup(); }
    if (target?.closest('[data-frost-vip-skip]') && !signupBusy) { showSignupError(''); showStep('complete'); }
  });
  if (dialog) {
    dialog.addEventListener('cancel', (event) => { event.preventDefault(); closeSignup(); });
    window.visualViewport?.addEventListener('resize', fitDialog);
    dialog.addEventListener('submit', async (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || !form.matches('[data-frost-vip-form]')) return;
      event.preventDefault();
      if (signupBusy) return;
      const intent = form.dataset.frostVipForm;
      const data = new FormData(form);
      data.set('intent', intent);
      data.set('source', location.pathname);
      data.set('country', config.country);
      data.set('language', config.language);
      if (intent === 'phone') data.set('receipt', receipt);
      signupBusy = true;
      showSignupError('');
      const button = form.querySelector('button[type=submit]');
      const label = button.textContent;
      button.disabled = true;
      button.textContent = intent === 'email' ? strings.sending : strings.saving;
      try {
        const endpoint = new URL(config.vipEndpoint);
        if (endpoint.protocol !== 'https:') throw new Error(strings.emailError);
        const response = await fetch(endpoint, { method: 'POST', body: data, credentials: 'omit' });
        let result;
        try { result = await response.json(); } catch { throw new Error(strings.emailError); }
        if (!response.ok || !result.ok) throw new Error(result.error || strings.emailError);
        if (intent === 'email') {
          if (!result.receipt) throw new Error(strings.emailError);
          receipt = result.receipt;
          dialog.querySelector('[data-frost-vip-email]').textContent = String(data.get('email'));
          remember({ subscribedUntil: Date.now() + 365 * day });
          showStep('phone');
        } else {
          receipt = '';
          showStep('complete');
        }
      } catch (error) { showSignupError(error.message || strings.emailError); }
      finally { signupBusy = false; button.disabled = false; button.textContent = label; }
    });
    const saved = preference();
    if (!config.designMode && !config.signedIn && !/\/(cart|checkout|account)(\/|$)/.test(location.pathname) &&
      !(saved.dismissedUntil > Date.now()) && !(saved.subscribedUntil > Date.now())) {
      let delayComplete = false;
      const maybeInvite = () => {
        if (shown || !delayComplete || document.visibilityState !== 'visible') return;
        const height = document.documentElement.scrollHeight - window.innerHeight;
        if (!location.pathname.includes('/products/') && !(height > 0 && window.scrollY / height >= .3)) return;
        if (document.querySelector('dialog[open], menu-drawer details[open], cart-drawer.active') ||
          ['INPUT', 'TEXTAREA', 'SELECT'].includes(document.activeElement?.tagName)) return;
        shown = true;
        if (window.matchMedia('(max-width: 680px)').matches) invitation.hidden = false;
        else openSignup();
        window.removeEventListener('scroll', maybeInvite);
      };
      window.setTimeout(() => { delayComplete = true; maybeInvite(); }, 15000);
      window.addEventListener('scroll', maybeInvite, { passive: true });
      document.addEventListener('visibilitychange', maybeInvite);
    }
  }
  window.FrostCommerce = Object.freeze({ openSignup, reconcile: () => serialized(reconcile) });
})();

